import { useState } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { PlansPlanMode } from '../../../api/types'
import { showAsFormattedRupeeAmount } from '../../../../display-values/lumpsumPlanAmount'
import type { LumpsumRebalanceSellPlanRecommendationDetails } from '../../api/rebalanceRecommendation.types'
import {
  areCounterFundsActionsCompleted,
  PlanRecommendationIntro
} from '../../shared/sheet/PlanRecommendationIntro'
import { PlanRecommendationSheetSkeleton } from '../../shared/sheet/PlanRecommendationSheetSkeleton'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { PlanRecommendationSheetLayout } from '../../shared/sheet/PlanRecommendationSheetLayout'
import { PlanRecommendationStatusReasonBannerSection } from '../../shared/sheet/PlanRecommendationStatusReasonBannerSection'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { RebalanceSellPlanRecommendationBody } from './body/RebalanceSellPlanRecommendationBody'
import { RebalanceSellPlanRecommendationHeader } from './header/RebalanceSellPlanRecommendationHeader'

type RebalanceSellPlanRecommendationSheetProps = {
  onDismiss: () => void
  isin: string
  planDetails: LumpsumRebalanceSellPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}

export function RebalanceSellPlanRecommendationSheet({
  onDismiss,
  isin,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  onMissedGainsInfoClick,
  transactionCta
}: RebalanceSellPlanRecommendationSheetProps) {
  const [selectedBuyIndex, setSelectedBuyIndex] = useState(0)

  // Sell-entry: root totalTransferAmount is sell; other side = sum of rounded buy card amounts.
  const sellAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  const buyAmount = (planDetails?.fundsToBeBought ?? []).reduce(
    (sum, fund) =>
      sum + showAsFormattedRupeeAmount({ rawAmount: fund?.buyAmount ?? 0 }).value,
    0
  )

  return (
    <PlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      planMode={PlansPlanMode.REBALANCE}
      analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
      fundName={planDetails?.fundName ?? ''}
      fundIsin={isin}
    >
      {!isLoading ? (
        <PlanRecommendationStatusReasonBannerSection planDetails={planDetails} analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation} />
      ) : null}
      {isLoading ? (
        <PlanRecommendationSheetSkeleton planMode={PlansPlanMode.REBALANCE} detailsTitle="Sell details" />
      ) : (
        <>
          <PlanRecommendationIntro
            planMode={PlansPlanMode.REBALANCE}
            buyAmount={buyAmount}
            sellAmount={sellAmount}
            areBuyActionsCompleted={areCounterFundsActionsCompleted(planDetails?.fundsToBeBought)}
            areSellActionsCompleted={planDetails?.executed === true}
            entryAction={LumpsumRecommendationActionType.SELL}
            counterFundCount={planDetails?.fundsToBeBought?.length ?? 0}
          />
          <RebalanceSellPlanRecommendationHeader
            planDetails={planDetails}
            entryIsin={isin}
            selectedBuyIndex={selectedBuyIndex}
            onSelectBuyIndex={setSelectedBuyIndex}
            transactionCta={transactionCta}
          />
          <RebalanceSellPlanRecommendationBody
            planDetails={planDetails}
            selectedBuyIndex={selectedBuyIndex}
            onSelectBuyIndex={setSelectedBuyIndex}
            onMissedGainsInfoClick={onMissedGainsInfoClick}
          />
        </>
      )}
    </PlanRecommendationSheetLayout>
  )
}
